// ── Document-number prefixes, browser side (multi-tenant package "docprefix") ─────────────────────────────
// Which prefix makes a number a quotation, an invoice or a receipt is the workspace's setting (Admin → Companies
// → <firm> → Document number prefixes; server single point services/docSeries.ts), fetched once from
// GET /api/doc-series. DocNoLink and every other screen ask here; nothing in the client spells a prefix.
// Until the answer arrives nothing is recognised (a number shows as plain text), then subscribers re-render.
import { useEffect, useSyncExternalStore } from 'react';
import { docPrefixApi, DocKind } from './api/docprefix';

export type { DocKind };

let prefixes: Record<string, DocKind> = {};
let splitter: RegExp | null = null;
let ready = false;
let inflight: Promise<void> | null = null;
let version = 0;
const subs = new Set<() => void>();
const NO_PREFIX = /^([A-Z0-9]+)-/;

function apply(map: Record<string, DocKind> | null | undefined): void {
  const next: Record<string, DocKind> = {};
  for (const [p, k] of Object.entries(map || {})) {
    if (/^[A-Z0-9]+$/.test(p) && (k === 'quotation' || k === 'invoice' || k === 'receipt')) next[p] = k;
  }
  prefixes = next;
  const alt = Object.keys(next).sort((a, b) => b.length - a.length || a.localeCompare(b)).join('|');
  // {prefix}-, then the firm letter and the digits (QO-L-20260900012); one capture group, so split() keeps the matches.
  splitter = alt ? new RegExp(`\\b((?:${alt})-[A-Z0-9]+(?:-[A-Z0-9]+)*)`) : null;
  ready = true;
  version++;
  subs.forEach(f => f());
}

/** Fetch the prefixes (once; `force` re-reads, e.g. after an admin saved new ones). A failure is retried on next use. */
export function loadDocSeries(force = false): Promise<void> {
  if (inflight) return inflight;
  if (ready && !force) return Promise.resolve();
  inflight = docPrefixApi.prefixMap()
    .then(r => apply(r?.prefixes))
    .catch(() => { /* not signed in yet / offline — the next use asks again */ })
    .finally(() => { inflight = null; });
  return inflight;
}

/** The kind a document number is, by its prefix; '' when it is not one of our numbers (or not loaded yet). */
export function docKindOf(no: any): DocKind | '' {
  const m = NO_PREFIX.exec(String(no ?? ''));
  return (m && prefixes[m[1]]) || '';
}

/** A regex with ONE capture group matching our document numbers inside text (for String.split); null until loaded. */
export function docNoSplitter(): RegExp | null {
  return splitter;
}

const subscribe = (f: () => void) => { subs.add(f); return () => { subs.delete(f); }; };
/** Re-render when the prefixes arrive or change; starts the fetch if nothing has been loaded yet. */
export function useDocSeries(): number {
  const v = useSyncExternalStore(subscribe, () => version);
  useEffect(() => { if (!ready) void loadDocSeries(); }, []);
  return v;
}
